import { Link } from "wouter";
import { ArrowLeft, Shield, AlertTriangle, Scale, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import { PageContainer } from "@/components/layout/PageContainer";

export default function Terms() {
  return (
    <PageContainer size="read">
        <Link href="/">
          <Button variant="ghost" className="mb-6" data-testid="button-back">
            <ArrowLeft className="h-4 w-4 mr-2" />
            목록으로
          </Button>
        </Link>

        <Card>
          <CardHeader>
            <CardTitle className="text-3xl font-bold">OZK News 이용약관</CardTitle>
            <p className="text-sm text-muted-foreground mt-2">
              최종 업데이트: 2025년 1월 | 버전 2.0
            </p>
          </CardHeader>
          <CardContent className="space-y-8 prose prose-sm max-w-none">
            <Alert>
              <Shield className="h-4 w-4" />
              <AlertDescription>
                본 서비스는 호주 연방법(Australian Consumer Law, Privacy Act 1988, Competition and Consumer Act 2010)을 준수합니다.
              </AlertDescription>
            </Alert>

            {/* Section 1 */}
            <section>
              <h2 className="text-xl font-bold flex items-center gap-2 mb-4">
                <Scale className="h-5 w-5 text-primary" />
                1. 약관의 적용 및 동의
              </h2>
              <div className="space-y-3 text-muted-foreground">
                <p>
                  본 약관은 OZK News(ozknews.com, 이하 '본 사이트')가 제공하는 모든 서비스(뉴스, 교민 업체주소록, 핫샵&딜, 중고마켓 등)의 이용에 적용됩니다.
                </p>
                <p>
                  <strong className="text-foreground">본 서비스를 이용하려면 18세 이상이어야 합니다.</strong> 18세 미만인 경우 부모 또는 보호자의 동의가 필요합니다.
                </p>
                <ul className="list-disc list-inside space-y-2">
                  <li>회원가입 시 필수 약관에 동의해야 합니다</li>
                  <li>약관에 동의하지 않으면 서비스를 이용할 수 없습니다</li>
                  <li>약관은 사전 공지 후 변경될 수 있으며, 변경된 약관은 공지 즉시 효력이 발생합니다</li>
                </ul>
              </div>
            </section>

            <Separator />

            {/* Section 2 - Marketplace Terms */}
            <section>
              <h2 className="text-xl font-bold flex items-center gap-2 mb-4">
                <AlertTriangle className="h-5 w-5 text-destructive" />
                2. 마켓플레이스 거래 안전 수칙 (중고마켓 & 핫샵&딜)
              </h2>

              <Alert variant="destructive" className="mb-4">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  <strong>중요:</strong> 본 사이트는 거래 중개 플랫폼일 뿐, 거래 당사자가 아닙니다.
                  모든 거래는 판매자와 구매자 간 직접 이루어지며, 사이트는 거래에 대한 책임을 지지 않습니다.
                </AlertDescription>
              </Alert>

              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold text-foreground mb-2">2.1 Australian Consumer Law (ACL) 준수</h3>
                  <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                    <li>판매자는 상품/서비스에 대해 허위 또는 오해의 소지가 있는 설명을 해서는 안 됩니다</li>
                    <li>판매자는 ACL에서 정한 소비자 보호 권리를 존중해야 합니다</li>
                    <li>구매자는 ACL에 따라 결함이 있는 상품에 대해 환불, 교환, 수리를 요구할 권리가 있습니다</li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-semibold text-foreground mb-2">2.2 사기 방지 가이드라인</h3>
                  <div className="bg-muted p-4 rounded-md space-y-2">
                    <p className="font-semibold text-destructive">⚠️ 다음과 같은 경우 절대 거래하지 마세요:</p>
                    <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                      <li>대면 거래를 거부하고 선입금만 요구하는 경우</li>
                      <li>시세보다 현저히 저렴한 가격을 제시하는 경우</li>
                      <li>급하게 거래를 종용하거나 압박하는 경우</li>
                      <li>이메일이나 전화번호가 의심스러운 경우</li>
                      <li>해외 송금을 요구하는 경우</li>
                      <li>개인정보(주민등록번호, 계좌 비밀번호)를 요구하는 경우</li>
                    </ul>
                  </div>
                </div>

                <div>
                  <h3 className="font-semibold text-foreground mb-2">2.3 안전한 거래 방법</h3>
                  <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                    <li><strong>대면 거래 권장:</strong> 공공장소(쇼핑센터, 경찰서 주차장 등)에서 낮 시간에 만나세요</li>
                    <li><strong>상품 확인:</strong> 거래 전 상품 상태를 직접 확인하세요</li>
                    <li><strong>결제 방법:</strong> 현금, 계좌이체, PayPal 등 안전한 방법을 사용하세요</li>
                    <li><strong>증거 보관:</strong> 대화 내용, 영수증, 사진 등을 보관하세요</li>
                    <li><strong>의심 신고:</strong> 의심스러운 게시글이나 사용자를 즉시 신고하세요</li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-semibold text-foreground mb-2">2.4 금지 품목</h3>
                  <p className="text-muted-foreground mb-2">호주 법령에 따라 다음 품목의 거래는 금지됩니다:</p>
                  <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                    <li>불법 약물, 무기류, 폭발물</li>
                    <li>도난 물품, 위조품, 해적판</li>
                    <li>담배, 전자담배 (면허 없이)</li>
                    <li>음란물, 불법 성인 콘텐츠</li>
                    <li>처방전 필요 의약품 (개인간 거래)</li>
                    <li>야생동물, 멸종위기 동식물</li>
                  </ul>
                </div>
              </div>
            </section>

            <Separator />

            {/* Section 3 - User Obligations */}
            <section>
              <h2 className="text-xl font-bold flex items-center gap-2 mb-4">
                <Users className="h-5 w-5 text-primary" />
                3. 이용자의 의무 및 금지사항
              </h2>
              <div className="space-y-3">
                <p className="text-muted-foreground">
                  모든 이용자는 다음 사항을 준수해야 합니다:
                </p>
                <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                  <li>실명 및 정확한 정보를 제공해야 합니다</li>
                  <li>타인의 개인정보를 무단으로 수집, 이용, 공개해서는 안 됩니다</li>
                  <li>허위 정보, 사기성 게시물을 작성해서는 안 됩니다</li>
                  <li>욕설, 비방, 명예훼손, 차별적 발언을 해서는 안 됩니다</li>
                  <li>스팸, 광고성 게시물을 무분별하게 게시해서는 안 됩니다</li>
                  <li>저작권을 침해하는 콘텐츠를 게시해서는 안 됩니다</li>
                  <li>시스템 해킹, 크롤링, 자동화 프로그램 사용을 금지합니다</li>
                </ul>

                <Alert className="mt-4">
                  <AlertDescription>
                    <strong>위반 시 제재:</strong> 경고 → 게시글 삭제 → 계정 정지(7일/30일) → 영구 정지
                  </AlertDescription>
                </Alert>
              </div>
            </section>

            <Separator />

            {/* Section 4 - Reports */}
            <section>
              <h2 className="text-xl font-bold mb-4">4. 신고 및 분쟁 해결</h2>
              <div className="space-y-3 text-muted-foreground">
                <p>
                  의심스러운 게시글, 사기 의심 거래, 부적절한 콘텐츠를 발견한 경우:
                </p>
                <ol className="list-decimal list-inside space-y-2">
                  <li><strong>사이트 내 신고:</strong> 각 게시글의 '신고하기' 버튼을 클릭하여 신고하세요</li>
                  <li><strong>증거 제출:</strong> 스크린샷, 대화 내용, 사진 등 증거를 첨부하세요</li>
                  <li><strong>관리자 검토:</strong> 신고는 24-48시간 내 검토됩니다</li>
                  <li><strong>조치:</strong> 위반 사항 확인 시 게시글 삭제, 계정 정지 등 조치가 취해집니다</li>
                </ol>

                <div className="bg-muted p-4 rounded-md mt-4">
                  <p className="font-semibold">🚨 심각한 범죄 행위(사기, 협박 등)는 호주 경찰에 신고하세요:</p>
                  <ul className="list-none space-y-1 mt-2 text-sm">
                    <li>• 긴급: 000 (Emergency)</li>
                    <li>• 비긴급: 131 444 (Police Assistance Line)</li>
                    <li>• 온라인 사기: <a href="https://www.scamwatch.gov.au" className="text-primary underline" target="_blank" rel="noopener noreferrer">Scamwatch</a></li>
                    <li>• ACCC 신고: <a href="https://www.accc.gov.au" className="text-primary underline" target="_blank" rel="noopener noreferrer">www.accc.gov.au</a></li>
                  </ul>
                </div>
              </div>
            </section>

            <Separator />

            {/* Section 5 - Privacy */}
            <section>
              <h2 className="text-xl font-bold mb-4">5. 개인정보 보호 (Privacy Act 1988)</h2>
              <div className="space-y-3 text-muted-foreground">
                <p>
                  본 사이트는 호주 개인정보 보호법(Privacy Act 1988)을 준수합니다:
                </p>
                <ul className="list-disc list-inside space-y-2">
                  <li>수집된 개인정보는 서비스 제공 목적으로만 사용됩니다</li>
                  <li>사용자 동의 없이 제3자에게 개인정보를 제공하지 않습니다</li>
                  <li>개인정보는 암호화되어 안전하게 보관됩니다</li>
                  <li>사용자는 언제든지 개인정보 열람, 수정, 삭제를 요청할 수 있습니다</li>
                </ul>
                <p className="mt-3">
                  자세한 내용은{" "}
                  <Link href="/privacy" className="text-primary underline">
                    개인정보처리방침
                  </Link>
                  을 참조하세요.
                </p>
              </div>
            </section>

            <Separator />

            {/* Section 6 - Copyright */}
            <section>
              <h2 className="text-xl font-bold mb-4">6. 저작권 및 지적재산권</h2>
              <div className="space-y-3 text-muted-foreground">
                <p>
                  본 사이트의 모든 콘텐츠(기사, 이미지, 로고, 디자인 등)는 저작권법으로 보호됩니다:
                </p>
                <ul className="list-disc list-inside space-y-2">
                  <li>개인적, 비상업적 목적의 열람 및 저장은 허용됩니다</li>
                  <li>무단 복제, 재배포, 상업적 이용은 금지됩니다</li>
                  <li>AI 생성 기사는 본 사이트의 저작물로 보호됩니다</li>
                  <li>외부 링크 콘텐츠의 저작권은 각 출처에 있습니다</li>
                </ul>
                <Alert className="mt-4">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    <strong>AI 생성 콘텐츠 고지:</strong> 본 사이트의 "쉬운 영어" 및 "퀴즈" 기능은 AI를 사용하여 생성됩니다.
                    AI 생성 콘텐츠의 정확성, 완전성, 적시성을 보장하지 않으며, 참고용으로만 사용하시기 바랍니다.
                    중요한 결정을 내리기 전에는 원문을 확인하시기 바랍니다.
                  </AlertDescription>
                </Alert>
              </div>
            </section>

            <Separator />

            {/* Section 7 - Liability */}
            <section>
              <h2 className="text-xl font-bold mb-4">7. 면책 조항</h2>
              <div className="space-y-3 text-muted-foreground">
                <p>
                  본 사이트는 다음 사항에 대해 책임을 지지 않습니다:
                </p>
                <ul className="list-disc list-inside space-y-2">
                  <li>사용자 간 거래(중고마켓, 핫샵&딜)에서 발생한 손해 및 분쟁</li>
                  <li>게시된 뉴스 및 정보의 정확성, 완전성, 적시성 (AI 생성 콘텐츠 포함)</li>
                  <li>외부 링크 사이트의 내용 및 안전성</li>
                  <li>시스템 장애, 네트워크 문제로 인한 서비스 중단</li>
                  <li>사용자의 귀책사유로 인한 개인정보 유출</li>
                </ul>
                <p className="mt-3">
                  <strong className="text-foreground">단, ACL에서 정한 소비자 권리는 제한되지 않습니다.</strong>
                </p>
              </div>
            </section>

            <Separator />

            {/* Section 8 - Jurisdiction */}
            <section>
              <h2 className="text-xl font-bold mb-4">8. 준거법 및 관할</h2>
              <div className="space-y-3 text-muted-foreground">
                <p>
                  본 약관은 호주 연방법 및 뉴사우스웨일즈(NSW) 주법을 준거법으로 합니다.
                </p>
                <p>
                  본 약관과 관련된 분쟁은 시드니 소재 법원의 전속 관할로 합니다.
                </p>
              </div>
            </section>

            <Separator />

            {/* Contact */}
            <section className="bg-muted p-6 rounded-lg">
              <h2 className="text-xl font-bold mb-4">문의사항</h2>
              <div className="space-y-2 text-muted-foreground">
                <p>약관 관련 문의사항이 있으시면 아래로 연락주세요:</p>
                <p>
                  <strong className="text-foreground">이메일:</strong> legal@ozknews.com
                </p>
                <p>
                  <strong className="text-foreground">운영시간:</strong> 월-금 9:00 AM - 6:00 PM (AEST)
                </p>
              </div>
            </section>

            <div className="text-center text-sm text-muted-foreground pt-6">
              <p>마지막 업데이트: 2025년 1월 19일</p>
              <p className="mt-2">이 약관에 동의하시면 서비스를 자유롭게 이용하실 수 있습니다.</p>
            </div>
          </CardContent>
        </Card>
    </PageContainer>
  );
}
